import { useRef, useState } from 'react';
import { z } from 'zod';
import { IconPicker } from '~/components/chat/commands/IconPicker';
import { KeyboardShortcut } from '~/components/chat/commands/KeyboardShortcut';
import * as PopupForm from '~/components/chat/commands/PopupForm';
import View from '~/components/chat/commands/View';
import { validateFormData } from '~/utils/form';

export const PromptCategoryCreate = ({
  onCancel,
  onCreate,
}: {
  onCancel?: () => void;
  onCreate: (data: { name: string; icon?: string }) => void;
}) => {
  const schema = z.object({
    name: z.string().min(1, 'Name is required'),
    icon: z.string().optional(),
  });

  const formRef = useRef<HTMLFormElement>(null);
  const [errors, setErrors] = useState<Partial<z.infer<typeof schema>>>({});
  const [name, setName] = useState<string>('');
  const [icon, setIcon] = useState<string>('i-lucide:message-square');

  function handleSubmit() {
    const formData = new FormData();

    formData.set('icon', icon);
    formData.set('name', name);

    validateFormData(formData, schema, {
      onSuccess: (data) => onCreate(data),
      onError: (errors) => setErrors(errors),
    });
  }

  return (
    <View.Container>
      <View.Header title="Create Prompt Category" />

      <View.Content>
        <PopupForm.Root ref={formRef}>
          <div className="flex gap-2">
            <IconPicker name="categoryIcon" label="Icon" value={icon} onValueChange={setIcon} />
            <PopupForm.Input
              label="Name"
              size="sm"
              name="name"
              autoFocus
              placeholder="Name your category..."
              className="w-full text-sm"
              error={errors.name}
              onChange={(event) => {
                setName(event.target.value);
                setErrors(({ name, ...rest }) => rest);
              }}
            />
          </div>
        </PopupForm.Root>
      </View.Content>

      <View.Footer>
        <KeyboardShortcut label="Cancel" keys={['Escape']} action={onCancel} />
        <KeyboardShortcut label="Create Category" keys={['Meta', 'Enter']} action={handleSubmit} />
      </View.Footer>
    </View.Container>
  );
};
